"use client";

import {
  CTribeDocument,
  CTribeDocumentFolder,
  CTribeDocumentParticipent,
  CTribeDocumentSpace,
} from "@/models/partner-document";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { Collections } from "../utils/collections";
import {
  defaultMap,
  deleteFirestoreCollection,
} from "../utils/utility-functions";

export async function getDocumentSpaces(): Promise<CTribeDocumentSpace[]> {
  try {
    const collectionRef = collection(db, Collections.CTRIBE_DOCUMENT_SPACES);
    const querySnapshot = await getDocs(collectionRef);
    return querySnapshot.docs.map((doc) =>
      defaultMap<CTribeDocumentSpace>(doc)
    );
  } catch (e) {
    return [];
  }
}

export async function getDocumentSpace(
  spaceId: string
): Promise<CTribeDocumentSpace | undefined> {
  try {
    const spaceRef = doc(db, Collections.CTRIBE_DOCUMENT_SPACES, spaceId);
    const spaceSnapshot = await getDoc(spaceRef);
    const space = defaultMap<CTribeDocumentSpace>(spaceSnapshot);
    space.folders = await getSpaceFolders(spaceId);
    return space;
  } catch (e) {
    return undefined;
  }
}

async function getSpaceFolders(
  spaceId: string
): Promise<CTribeDocumentFolder[]> {
  try {
    const collectionRef = collection(
      db,
      Collections.CTRIBE_DOCUMENT_SPACES,
      spaceId,
      Collections.SPACE_FOLDERS
    );
    const querySnapshot = await getDocs(collectionRef);
    if (querySnapshot.empty) {
      return [];
    }
    const folders = querySnapshot.docs.map((doc) =>
      defaultMap<CTribeDocumentFolder>(doc)
    );
    await Promise.all(
      folders.map(async (folder) => {
        folder.documents = [];
        if (folder.documentRefs) {
          await Promise.all(
            folder.documentRefs.map(async ({ documentId, isVisible }) => {
              const result = await getCTribeDocument(documentId);
              if (result) {
                folder.documents?.push({ ...result, isVisible });
              }
            })
          );
        }
      })
    );
    return folders.sort((a, b) => a.sequence - b.sequence);
  } catch (e) {
    return [];
  }
}

async function getCTribeDocument(
  docId: string
): Promise<CTribeDocument | null> {
  try {
    const docRef = doc(db, Collections.CTRIBE_DOCUMENTS, docId);
    const docSnapshot = await getDoc(docRef);
    const cTribeDoc = defaultMap<CTribeDocument>(docSnapshot);
    return cTribeDoc;
  } catch (e) {
    return null;
  }
}

export async function getCTribeDocuments(): Promise<CTribeDocument[]> {
  try {
    const collectionRef = collection(db, Collections.CTRIBE_DOCUMENTS);
    const querySnapshot = await getDocs(collectionRef);
    return querySnapshot.docs.map((doc) => defaultMap<CTribeDocument>(doc));
  } catch (e) {
    return [];
  }
}

export async function saveDocument(document: CTribeDocument) {
  try {
    const docRef = document.id
      ? await updateDocument(document)
      : await addDocument(document);
    const docSnapshot = await getDoc(docRef);
    return defaultMap<CTribeDocument>(docSnapshot);
  } catch (e) {
    return null;
  }
}

export async function addDocument(document: CTribeDocument) {
  const collectionRef = collection(db, Collections.CTRIBE_DOCUMENTS);
  const { id, ...rest } = document;
  return await addDoc(collectionRef, { ...rest });
}

export async function updateDocument(document: CTribeDocument) {
  const docRef = doc(db, Collections.CTRIBE_DOCUMENTS, document.id);
  const { id, ...rest } = document;
  await updateDoc(docRef, { ...rest });
  return docRef;
}

export async function deleteDocument(document: CTribeDocument) {
  try {
    const docRef = doc(db, Collections.CTRIBE_DOCUMENTS, document.id);
    await deleteDoc(docRef);
    return true;
  } catch (e) {
    return false;
  }
}

export async function saveDocumentSpace(space: CTribeDocumentSpace) {
  try {
    const spaceRef = space.id
      ? await updateDocumentSpace(space)
      : await addDocumentSpace(space);
    const spaceSnapshot = await getDoc(spaceRef);
    return defaultMap<CTribeDocumentSpace>(spaceSnapshot);
  } catch (e) {
    return null;
  }
}

async function addDocumentSpace(space: CTribeDocumentSpace) {
  const collectionRef = collection(db, Collections.CTRIBE_DOCUMENT_SPACES);
  const { id, folders, participients, ...rest } = space;
  return await addDoc(collectionRef, { ...rest });
}

async function updateDocumentSpace(space: CTribeDocumentSpace) {
  const docRef = doc(db, Collections.CTRIBE_DOCUMENT_SPACES, space.id);
  const { id, folders, participients, ...rest } = space;
  await updateDoc(docRef, { ...rest });
  return docRef;
}

export async function deleteDocumentSpace(space: CTribeDocumentSpace) {
  try {
    await deleteFirestoreCollection(
      `${Collections.CTRIBE_DOCUMENT_SPACES}/${space.id}/${Collections.SPACE_FOLDERS}`
    );
    await deleteFirestoreCollection(
      `${Collections.CTRIBE_DOCUMENT_SPACES}/${space.id}/${Collections.SPACE_PARTICIPANTS}`
    );
    const docRef = doc(db, Collections.CTRIBE_DOCUMENT_SPACES, space.id);
    await deleteDoc(docRef);
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
}

export async function saveDocumentFolder(
  spaceId: string,
  folder: CTribeDocumentFolder
) {
  try {
    const folderRef = folder.id
      ? await updateDocumentFolder(spaceId, folder)
      : await addDocumentFolder(spaceId, folder);
    const folderSnapshot = await getDoc(folderRef);
    return defaultMap<CTribeDocumentFolder>(folderSnapshot);
  } catch (e) {
    return null;
  }
}

async function addDocumentFolder(
  spaceId: string,
  folder: CTribeDocumentFolder
) {
  const collectionRef = collection(
    db,
    Collections.CTRIBE_DOCUMENT_SPACES,
    spaceId,
    Collections.SPACE_FOLDERS
  );
  const { id, documents, ...rest } = folder;
  return await addDoc(collectionRef, { ...rest });
}

async function updateDocumentFolder(
  spaceId: string,
  folder: CTribeDocumentFolder
) {
  const docRef = doc(
    db,
    Collections.CTRIBE_DOCUMENT_SPACES,
    spaceId,
    Collections.SPACE_FOLDERS,
    folder.id
  );
  const { id, documents, ...rest } = folder;
  await updateDoc(docRef, { ...rest });
  return docRef;
}

export async function deleteDocumentFolder(spaceId: string, folderId: string) {
  try {
    const docRef = doc(
      db,
      Collections.CTRIBE_DOCUMENT_SPACES,
      spaceId,
      Collections.SPACE_FOLDERS,
      folderId
    );
    await deleteDoc(docRef);
    return true;
  } catch (e) {
    return false;
  }
}

export async function getSpaceParticipants(spaceId: string) {
  try {
    const collectionRef = collection(
      db,
      Collections.CTRIBE_DOCUMENT_SPACES,
      spaceId,
      Collections.SPACE_PARTICIPANTS
    );
    const querySnapshot = await getDocs(collectionRef);
    if (querySnapshot.empty) {
      return [];
    }
    return querySnapshot.docs.map((doc) =>
      defaultMap<CTribeDocumentParticipent>(doc)
    );
  } catch (e) {
    return [];
  }
}

export async function addSpaceParticipant(payload: CTribeDocumentParticipent) {
  try {
    const collectionRef = collection(
      db,
      Collections.CTRIBE_DOCUMENT_SPACES,
      payload.spaceId,
      Collections.SPACE_PARTICIPANTS
    );
    const { id, ...rest } = payload;
    const docRef = await addDoc(collectionRef, {
      ...rest,
      timestamp: serverTimestamp(),
    });
    const docSnapshot = await getDoc(docRef);
    return defaultMap<CTribeDocumentParticipent>(docSnapshot);
  } catch (e) {
    return null;
  }
}

export async function deleteSpaceParticipant(spaceId: string, docId: string) {
  try {
    const docRef = doc(
      db,
      Collections.CTRIBE_DOCUMENT_SPACES,
      spaceId,
      Collections.SPACE_PARTICIPANTS,
      docId
    );
    await deleteDoc(docRef);
    return true;
  } catch (e) {
    return false;
  }
}
